// src/screens/SeguimientoServicioScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
    IoArrowBack, 
    IoMapOutline, 
    IoChatbubbleOutline, 
    IoCheckmarkCircleOutline, 
    IoCameraOutline, 
    IoQrCodeOutline, 
    IoTimeOutline, 
    IoLocationOutline, 
    IoCloseCircleOutline, 
    IoAlertCircleOutline 
} from 'react-icons/io5';
import moment from 'moment';
import QRCode from 'react-qr-code';
import { useAuth } from '../context/AuthContext';
import './SeguimientoServicioScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function SeguimientoServicioScreen() {
    const navigate = useNavigate();
    const { cotizacionId } = useParams();
    const { userData } = useAuth();

    const [cotizacion, setCotizacion] = useState(null);
    const [proveedorData, setProveedorData] = useState(null);
    const [condominioData, setCondominioData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enviando, setEnviando] = useState(false);

    // Estados para Presupuesto Final
    const [costoFinal, setCostoFinal] = useState('');
    const [descripcionFinal, setDescripcionFinal] = useState('');

    // Estados para Evidencia Final
    const [archivos, setArchivos] = useState([]);
    const [previews, setPreviews] = useState([]);

    // Estado para Acceso
    const [acceso, setAcceso] = useState(null);
    const [showQR, setShowQR] = useState(false);

    // Modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');

    // Fixed gradient for this screen (Blue)
    const gradient = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';

    // Definición del Workflow (Pasos)
    const workflowSteps = [
        { label: 'Solicitud', statuses: ['pendiente'] },
        { label: 'Cotización', statuses: ['pendiente_residente'] },
        { label: 'Programado', statuses: ['aceptada', 'servicio_programado'] },
        { label: 'En Camino', statuses: ['proveedor_en_camino'] },
        { label: 'En Sitio', statuses: ['proveedor_en_sitio'] },
        { label: 'Aprobación', statuses: ['esperando_aprobacion_final'] },
        { label: 'En Curso', statuses: ['servicio_en_curso'] },
        { label: 'Terminado', statuses: ['servicio_terminado', 'completada', 'completado'] }
    ];

    // Escuchar si existe un acceso generado para esta cotización
    useEffect(() => {
        if (!cotizacionId) return;
        const q = query(
            collection(db, "accesos"),
            where("cotizacionId", "==", cotizacionId),
            where("estatus", "==", "Activo")
        );
        const unsubscribe = onSnapshot(q, (snap) => {
            if (!snap.empty) {
                setAcceso({ id: snap.docs[0].id, ...snap.docs[0].data() });
            } else {
                setAcceso(null);
            }
        }, (error) => {
            console.log("Error accesos:", error);
        });
        return () => unsubscribe();
    }, [cotizacionId]);

    useEffect(() => {
        const fetchData = async () => {
            if (!cotizacionId) return;
            try {
                const cotRef = doc(db, "cotizaciones", cotizacionId);
                const cotSnap = await getDoc(cotRef);

                if (cotSnap.exists()) {
                    const data = cotSnap.data();
                    setCotizacion({ id: cotSnap.id, ...data });

                    // Cargar datos del proveedor
                    if (data.proveedorId) {
                        const provRef = doc(db, "market_proveedores", data.proveedorId);
                        const provSnap = await getDoc(provRef);
                        if (provSnap.exists()) {
                            setProveedorData(provSnap.data());
                        }
                    }

                    // Cargar datos del condominio
                    if (data.condominioId) {
                        const condoRef = doc(db, "condominios", data.condominioId);
                        const condoSnap = await getDoc(condoRef);
                        if (condoSnap.exists()) {
                            setCondominioData(condoSnap.data());
                        }
                    }
                }
            } catch (error) {
                console.error("Error cargando datos:", error);
            }
            setLoading(false);
        };
        fetchData();
    }, [cotizacionId]);

    const handleStatusChange = async (nuevoStatus, extraData = {}) => {
        setEnviando(true);
        try {
            const cotRef = doc(db, "cotizaciones", cotizacionId);
            await updateDoc(cotRef, {
                estatus: nuevoStatus,
                ...extraData
            });
            setCotizacion(prev => ({ ...prev, estatus: nuevoStatus, ...extraData }));
        } catch (error) {
            console.error("Error actualizando estatus:", error);
            setModalTitle("Error");
            setModalMessage("Error al actualizar: " + error.message);
            setShowSuccessModal(true);
        }
        setEnviando(false);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setArchivos(files);
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setPreviews(newPreviews);
    };

    const handleEnviarPresupuesto = async (e) => {
        e.preventDefault();
        if (!costoFinal) {
            setModalTitle("Información Faltante");
            setModalMessage("Debes ingresar el costo del presupuesto.");
            setShowSuccessModal(true);
            return;
        }

        setEnviando(true);
        let urls = [];
        try {
            for (const file of archivos) {
                const storageRef = ref(storage, `evidencia_antes/${cotizacionId}/${Date.now()}_${file.name}`);
                const snapshot = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(snapshot.ref);
                urls.push(url);
            }

            await handleStatusChange('esperando_aprobacion_final', {
                costoFinal: Number(costoFinal),
                descripcionFinal: descripcionFinal,
                fechaPresupuestoFinal: serverTimestamp(),
                evidenciaAntesUrls: urls
            });

            setModalTitle("Presupuesto Enviado");
            setModalMessage("El presupuesto final ha sido enviado exitosamente al residente para su aprobación.");
            setShowSuccessModal(true);
        } catch (error) {
            console.error(error);
            setModalTitle("Error");
            setModalMessage("Error al procesar el presupuesto.");
            setShowSuccessModal(true);
        }
        setEnviando(false);
    };

    const handleFinalizarServicio = async () => {
        if (archivos.length === 0 && !window.confirm("¿Confirmar sin evidencia?")) return;

        setEnviando(true);
        let urls = [];
        try {
            for (const file of archivos) {
                const storageRef = ref(storage, `evidencia_final/${cotizacionId}/${Date.now()}_${file.name}`);
                const snapshot = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(snapshot.ref);
                urls.push(url);
            }

            await handleStatusChange('servicio_terminado', {
                evidenciaFinalUrls: urls,
                fechaFin: serverTimestamp()
            });

            setModalTitle("Servicio Finalizado");
            setModalMessage("El servicio ha sido marcado como terminado correctamente y la evidencia fue guardada.");
            setShowSuccessModal(true);
        } catch (e) {
            console.error(e);
            setModalTitle("Error");
            setModalMessage("Error al finalizar el servicio.");
            setShowSuccessModal(true);
        }
        setEnviando(false);
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        if (modalTitle === "Servicio Finalizado") {
            navigate('/proveedor/servicios');
        }
    };

    const abrirMapa = () => {
        if (condominioData?.direccion) {
            const query = encodeURIComponent(condominioData.direccion);
            window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
        }
    };

    const getStepClass = (stepRequestStatuses) => {
        if (!cotizacion) return 'status-step';

        const currentStepIndex = workflowSteps.findIndex(step => step.statuses.includes(cotizacion.estatus));
        const renderStepIndex = workflowSteps.findIndex(step => JSON.stringify(step.statuses) === JSON.stringify(stepRequestStatuses));
        const isCancelled = cotizacion.estatus.includes('cancelado') || cotizacion.estatus.includes('rechazado') || cotizacion.estatus.includes('rechazada');

        if (isCancelled) {
            let previousStatusIdx = -1;
            if (cotizacion.estatusPrevio) {
                previousStatusIdx = workflowSteps.findIndex(step => step.statuses.includes(cotizacion.estatusPrevio));
            }

            if (previousStatusIdx !== -1) {
                if (renderStepIndex <= previousStatusIdx) return 'status-step completed';
                if (renderStepIndex === previousStatusIdx + 1) return 'status-step cancelled-point';
                return 'status-step inactive';
            } else {
                return 'status-step cancelled-view';
            }
        }

        if (currentStepIndex === -1) return 'status-step';
        if (currentStepIndex > renderStepIndex) return 'status-step completed';
        if (currentStepIndex === renderStepIndex) return 'status-step active';

        return 'status-step';
    };

    const isCancelled = cotizacion?.estatus?.includes('cancelado') || cotizacion?.estatus?.includes('rechazado') || cotizacion?.estatus?.includes('rechazada');
    const cancelDate = cotizacion?.fechaCancelacion?.toDate ? cotizacion.fechaCancelacion.toDate() : (cotizacion?.lastUpdate?.toDate ? cotizacion.lastUpdate.toDate() : new Date());

    if (loading) return <div className="tracking-container"><div className="loader"></div></div>;
    if (!cotizacion) return <div className="tracking-container"><p>No se encontró el servicio.</p></div>;

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/proveedor/servicios" />
            
            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area">
                    <div className="tracking-container" style={{ padding: '20px 10px', maxWidth: '800px', margin: '0 auto' }}>
                        
                        {/* Premium Header Banner */}
                        <div style={{ background: gradient, position: 'relative', overflow: 'hidden', padding: '30px 25px', borderRadius: '24px', marginBottom: '30px', boxShadow: '0 12px 30px rgba(37, 99, 235, 0.15)', color: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <button onClick={() => navigate('/proveedor/servicios')} style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'all 0.2s', color: 'white', flexShrink: 0 }} className="encuestas-glass-btn">
                                        <IoArrowBack size={22} />
                                    </button>
                                    <div>
                                        <h1 style={{ color: 'white', margin: '0 0 6px', fontSize: '1.7rem', fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>Seguimiento del Servicio</h1>
                                        <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.95rem', fontWeight: '500' }}>{cotizacion.tipoServicio}</p>
                                    </div>
                                </div>
                                {isCancelled && <span style={{ background: '#ef4444', color: 'white', padding: '6px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold' }}>Cancelado</span>}
                            </div>
                            <div style={{ position: 'absolute', right: '-15px', bottom: '-40px', zIndex: 0, transform: 'rotate(-10deg)', opacity: 0.15, pointerEvents: 'none' }}>
                                <IoMapOutline size={150} color="white" />
                            </div>
                        </div>

                        {/* Timeline Wrapper */}
                        <div className="status-timeline" style={{ borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                            {workflowSteps.map((step, index) => {
                                const stepClass = getStepClass(step.statuses);
                                return (
                                    <div key={index} className={stepClass}>
                                        <div className="step-circle">
                                            {stepClass.includes('cancelled-point') ? <IoCloseCircleOutline size={20} /> : (index + 1)}
                                        </div>
                                        <span className="step-label">{step.label}</span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Access Token Alert */}
                        {acceso && (
                            <div className="access-token-box" onClick={() => setShowQR(!showQR)} style={{ cursor: 'pointer', borderRadius: '24px', border: '1px solid #a7f3d0' }}>
                                <div className="token-info">
                                    <div className="token-icon" style={{ borderRadius: '12px' }}><IoQrCodeOutline /></div>
                                    <div>
                                        <h4 style={{ margin: 0, color: '#065f46', fontWeight: '800' }}>Acceso Autorizado</h4>
                                        <p style={{ margin: 0, color: '#047857', fontSize: '0.85rem' }}>Toca para ver código QR</p>
                                    </div>
                                </div>
                                <div className="token-code" style={{ borderRadius: '12px' }}>{acceso.token}</div>

                                {showQR && (
                                    <div style={{ marginTop: 15, background: 'white', padding: 15, borderRadius: '16px', display: 'flex', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }} onClick={e => e.stopPropagation()}>
                                        <QRCode value={acceso.token} size={150} />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="tracking-grid" style={{ gap: '24px' }}>
                            {/* Left Col: Info */}
                            <div className="tracking-card" style={{ borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                                <h3 style={{ fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>Detalles del Servicio</h3>
                                <div className="tracking-info-row">
                                    <span className="tracking-info-label">Residente</span>
                                    <span className="tracking-info-value">{cotizacion.nombreResidente}</span>
                                </div>
                                <div className="tracking-info-row">
                                    <span className="tracking-info-label">Condominio</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <span className="tracking-info-value">{cotizacion.nombreCondominio}</span>
                                        {condominioData?.direccion && (
                                            <button style={{ display: 'block', fontSize: '0.85rem', color: '#2563eb', border: 'none', background: 'none', cursor: 'pointer', marginLeft: 'auto', fontWeight: '600', marginTop: '4px' }} onClick={abrirMapa}>
                                                Ver Ubicación
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="tracking-info-row">
                                    <span className="tracking-info-label">Servicio</span>
                                    <span className="tracking-info-value">{cotizacion.tipoServicio}</span>
                                </div>
                                <div className="tracking-info-row" style={{ borderBottom: 'none' }}>
                                    <span className="tracking-info-label">Cita Sugerida</span>
                                    <span className="tracking-info-value">{cotizacion.fechaHoraServicioSugerido ? moment(cotizacion.fechaHoraServicioSugerido.toDate()).format('D MMM HH:mm') : 'Pendiente'}</span>
                                </div>
                            </div>

                            {/* Right Col: Action Layout */}
                            <div className="tracking-card" style={{ borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                                {!isCancelled ? (
                                    <>
                                        <h3 style={{ fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>Acciones del Proveedor</h3>

                                        {/* STATE 0: Pendiente (Solicitud) */}
                                        {cotizacion.estatus === 'pendiente' && (
                                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                                <IoTimeOutline size={50} color="#6b7280" />
                                                <h4 style={{ color: '#374151', margin: '12px 0 6px', fontWeight: '700' }}>Solicitud Recibida</h4>
                                                <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 20px' }}>El residente ha solicitado este servicio, pero aún no has enviado una propuesta.</p>
                                                <button className="btn-primary-action" style={{ borderRadius: '12px' }} onClick={() => navigate(`/proveedor/responder/${cotizacionId}`)}>
                                                    Responder Cotización
                                                </button>
                                            </div>
                                        )}

                                        {/* STATE 1: Cotizada (Esperando al residente) */}
                                        {cotizacion.estatus === 'pendiente_residente' && (
                                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                                <IoTimeOutline size={50} color="#3b82f6" />
                                                <h4 style={{ color: '#1e40af', margin: '12px 0 6px', fontWeight: '700' }}>Cotización Enviada</h4>
                                                <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 16px' }}>Tu propuesta ha sido enviada. Esperando que el residente la acepte.</p>
                                                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                                                    <small style={{ display: 'block', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>Tu propuesta:</small>
                                                    <strong style={{ fontSize: '1.1rem', color: '#1e293b' }}>${cotizacion.costoMin} - ${cotizacion.costoMax}</strong>
                                                </div>
                                            </div>
                                        )}

                                        {/* STATE 2: Programado / Aceptada */}
                                        {(cotizacion.estatus === 'aceptada' || cotizacion.estatus === 'servicio_programado') && (
                                            <div>
                                                <p style={{ color: '#4b5563', marginBottom: 15, fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                    El servicio está confirmado. Cuando salgas hacia el domicilio, notifícalo.
                                                </p>
                                                <button className="btn-primary-action" style={{ borderRadius: '12px' }} onClick={() => handleStatusChange('proveedor_en_camino')} disabled={enviando}>
                                                    <IoMapOutline /> Voy en Camino
                                                </button>
                                                <div style={{ display: 'flex', gap: 10, marginTop: 15 }}>
                                                    <button className="btn-secondary-action" style={{ borderRadius: '12px' }} onClick={() => alert("Próximamente Chat")}>
                                                        <IoChatbubbleOutline /> Chat
                                                    </button>
                                                    <button className="btn-secondary-action" style={{ borderRadius: '12px' }} onClick={abrirMapa}>
                                                        <IoLocationOutline /> Ruta GPS
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* STATE 3: En Camino */}
                                        {cotizacion.estatus === 'proveedor_en_camino' && (
                                            <div>
                                                <p style={{ color: '#4b5563', marginBottom: 15, fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                    Has notificado que vas en ruta. Presiona el botón al llegar a la caseta o domicilio.
                                                </p>
                                                <button className="btn-primary-action" style={{ borderRadius: '12px' }} onClick={() => handleStatusChange('proveedor_en_sitio', { fechaLlegada: serverTimestamp() })} disabled={enviando}>
                                                    <IoCheckmarkCircleOutline /> ¡Ya llegué!
                                                </button>
                                            </div>
                                        )}

                                        {/* STATE 4: En Sitio (Presupuesto) */}
                                        {cotizacion.estatus === 'proveedor_en_sitio' && (
                                            <div>
                                                <p style={{ color: '#4b5563', marginBottom: 15, fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                    Revisa el trabajo y define el costo final exacto. Sube foto "Antes".
                                                </p>
                                                <form onSubmit={handleEnviarPresupuesto} className="action-area" style={{ borderRadius: '16px', background: '#fafafa', border: '1px solid #cbd5e1' }}>
                                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: '0.9rem' }}>Costo Final Total ($)</label>
                                                    <input
                                                        type="number"
                                                        className="tracking-input"
                                                        value={costoFinal}
                                                        onChange={e => setCostoFinal(e.target.value)}
                                                        placeholder="0.00"
                                                        style={{ borderRadius: '10px' }}
                                                    />

                                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: '0.9rem' }}>Evidencia Inicial</label>
                                                    <div className="tracking-file-upload" style={{ borderRadius: '10px' }}>
                                                        <input type="file" multiple accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} id="file-before" />
                                                        <label htmlFor="file-before" style={{ cursor: 'pointer', color: '#2563eb' }}>
                                                            <IoCameraOutline size={24} />
                                                            <div style={{ fontWeight: '600', marginTop: '6px' }}>Tocar para subir fotos</div>
                                                        </label>
                                                    </div>
                                                    {previews.length > 0 && (
                                                        <div className="gallery-preview" style={{ marginBottom: '15px' }}>
                                                            {previews.map((src, i) => <img key={i} src={src} alt="preview" style={{ borderRadius: '8px' }} />)}
                                                        </div>
                                                    )}

                                                    <label style={{ display: 'block', marginBottom: 8, marginTop: 10, fontWeight: 600, fontSize: '0.9rem' }}>Notas</label>
                                                    <textarea className="tracking-input" rows={2} value={descripcionFinal} onChange={e => setDescripcionFinal(e.target.value)} style={{ borderRadius: '10px' }} />

                                                    <button type="submit" className="btn-primary-action" style={{ borderRadius: '12px' }} disabled={enviando}>
                                                        Enviar a Aprobación
                                                    </button>
                                                </form>
                                            </div>
                                        )}

                                        {/* STATE 5: Esperando Aprobación */}
                                        {cotizacion.estatus === 'esperando_aprobacion_final' && (
                                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                                <IoTimeOutline size={50} color="#f59e0b" />
                                                <h4 style={{ color: '#b45309', margin: '12px 0 6px', fontWeight: '700' }}>Esperando confirmación</h4>
                                                <p style={{ margin: '0 0 10px' }}>Monto enviado: <strong style={{ color: '#1e293b' }}>${cotizacion.costoFinal}</strong></p>
                                                <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>El residente debe aprobar y pagar para iniciar.</p>
                                            </div>
                                        )}

                                        {/* STATE 6: En Curso */}
                                        {cotizacion.estatus === 'servicio_en_curso' && (
                                            <div>
                                                <div style={{ background: '#ecfdf5', borderRadius: '12px', padding: 15, marginBottom: 15, border: '1px solid #a7f3d0' }}>
                                                    <h4 style={{ margin: 0, color: '#065f46', fontWeight: '800' }}>¡Servicio Aprobado!</h4>
                                                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#047857' }}>Puedes realizar el trabajo ahora.</p>
                                                </div>
                                                <p style={{ color: '#4b5563', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                    Al terminar, sube evidencia final para cerrar la orden y liberar tu pago.
                                                </p>

                                                <div className="action-area" style={{ borderRadius: '16px', background: '#fafafa', border: '1px solid #cbd5e1' }}>
                                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: '0.9rem' }}>Evidencia Trabajo Terminado</label>
                                                    <div className="tracking-file-upload" style={{ borderRadius: '10px' }}>
                                                        <input type="file" multiple accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} id="file-after" />
                                                        <label htmlFor="file-after" style={{ cursor: 'pointer', color: '#2563eb' }}>
                                                            <IoCameraOutline size={24} />
                                                            <div style={{ fontWeight: '600', marginTop: '6px' }}>Subir fotos finales</div>
                                                        </label>
                                                    </div>
                                                    {previews.length > 0 && (
                                                        <div className="gallery-preview" style={{ marginBottom: '15px' }}>
                                                            {previews.map((src, i) => <img key={i} src={src} alt="preview" style={{ borderRadius: '8px' }} />)}
                                                        </div>
                                                    )}
                                                    <button className="btn-primary-action" style={{ background: '#10b981', borderRadius: '12px', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }} onClick={handleFinalizarServicio} disabled={enviando}>
                                                        <IoCheckmarkCircleOutline /> Finalizar Servicio
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* STATE 7: Terminado */}
                                        {(cotizacion.estatus === 'servicio_terminado' || cotizacion.estatus === 'completada' || cotizacion.estatus === 'completado') && (
                                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                                <IoCheckmarkCircleOutline size={60} color="#10b981" />
                                                <h2 style={{ color: '#065f46', margin: '15px 0 6px', fontWeight: '800' }}>¡Servicio Completado!</h2>
                                                <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 20px' }}>El proceso ha finalizado correctamente.</p>
                                                <button className="btn-secondary-action" style={{ margin: '0 auto', borderRadius: '12px' }} onClick={() => navigate('/proveedor/servicios')}>
                                                    Volver al Historial
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* CANCELLATION DETAILS CARD */
                                    <div className="cancellation-details">
                                        <div className="cancel-icon-wrapper" style={{ borderRadius: '50%' }}>
                                            <IoCloseCircleOutline size={50} color="#ef4444" />
                                        </div>
                                        <h3 style={{ color: '#ef4444', fontWeight: '800' }}>Servicio Cancelado</h3>

                                        <div className="cancel-detail-row">
                                            <span className="cancel-label">Motivo:</span>
                                            <span className="cancel-value">{cotizacion.motivoCancelacion || "El residente rechazó o canceló la solicitud."}</span>
                                        </div>

                                        <div className="cancel-detail-row">
                                            <span className="cancel-label">Fecha:</span>
                                            <span className="cancel-value">{moment(cancelDate).format('D MMM YYYY, h:mm a')}</span>
                                        </div>

                                        {cotizacion.canceladoPor && (
                                            <div className="cancel-detail-row">
                                                <span className="cancel-label">Cancelado por:</span>
                                                <span className="cancel-value" style={{ textTransform: 'capitalize' }}>{cotizacion.canceladoPor}</span>
                                            </div>
                                        )}

                                        {cotizacion.estatusPrevio && (
                                            <div className="cancel-detail-row">
                                                <span className="cancel-label">Etapa alcanzada:</span>
                                                <span className="cancel-value">{cotizacion.estatusPrevio.replace(/_/g, ' ')}</span>
                                            </div>
                                        )}

                                        <div className="cancel-alert-box" style={{ borderRadius: '12px' }}>
                                            <IoAlertCircleOutline size={20} />
                                            <p style={{ margin: 0 }}>Si crees que esto es un error, contacta a la administración.</p>
                                        </div>

                                        <button className="btn-secondary-action" style={{ width: '100%', justifyContent: 'center', marginTop: 20, borderRadius: '12px' }} onClick={() => navigate('/proveedor/servicios')}>
                                            Volver al Historial
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Custom Success Modal */}
            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(6px)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 99999
                }}>
                    <div style={{
                        background: 'white', padding: '35px 30px', borderRadius: '28px',
                        width: '90%', maxWidth: '400px', textAlign: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        fontFamily: "'Inter', sans-serif"
                    }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            backgroundColor: '#dcfce7', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px', color: '#16a34a',
                            boxShadow: '0 8px 16px rgba(22, 163, 74, 0.15)'
                        }}>
                            <IoCheckmarkCircleOutline size={36} />
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.4rem', fontWeight: '800', color: '#0f172a' }}>
                            {modalTitle}
                        </h3>
                        <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                            {modalMessage}
                        </p>
                        <button
                            onClick={handleModalClose}
                            style={{
                                width: '100%', padding: '14px 24px', border: 'none',
                                borderRadius: '14px', background: '#3b82f6', color: 'white',
                                fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            <KondoBottomNav activeRoute="/proveedor/servicios" />
        </div>
    );
}

export default SeguimientoServicioScreen;
